import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, Inject, OnDestroy, OnInit, signal, viewChild } from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';

import { DateTime } from 'luxon';
import { add } from 'date-fns';

import {
  Observable,
  Subject,
  catchError,
  combineLatest,
  delay,
  forkJoin,
  last,
  map,
  mergeMap,
  of,
  switchMap,
  take,
  takeUntil,
  takeWhile,
  timeout,
  timer
} from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';

import { ToastType, NotificationToastService } from '@sondermind/features/notification-toast';
import { TooltipModule } from '@sondermind/features/tooltip';
import {
  ClientPortalReferralsStore,
  ClientReferralMember,
  ClientReferralMemberStatus,
  ClientReferralMembersStore,
  IProvider,
  Provider,
  ProviderStore
} from '@sondermind/data-access/client-portal-referrals';
import { ClientReferralLite } from '@sondermind/data-access/client-referral';
import { Appointment, AppointmentsHttpService } from '@sondermind/data-access/appointments';
import { CreditCard, CreditCardStore } from '@sondermind/data-access/client-credit-card';
import {
  SegmentService,
  IClientSegmentSchema,
  ClientSegmentEvents,
  IDirectBookingStartedData
} from '@sondermind/segment';
import {
  IBenefitsInformation,
  ITempImprovementPatientInsurances,
  PatientInsuranceStore
} from '@sondermind/data-access/client-insurance';
import { ICostEstimateContextAttributes } from '@sondermind/utilities/models-client-insurance';
import { CurrentUser } from '@sondermind/data-access/client-user';
import { DirectScheduleSessionHttpService } from '@sondermind/data-access/direct-scheduling';
import { IAppointmentSlot } from '@sondermind/data-access/public-facing/provider-calendar';
import {
  ProviderAvailabilityMember,
  ProviderRelationship,
  ScheduledEventsHttpService,
  SchedulingAvailabilityService,
  TimeSlot
} from '@sondermind/data-access/scheduling';
import { Logger } from '@sondermind/datadog-logging';
import { CoverageNotificationService } from '@sondermind/features/account-edit';
import { ProviderPhotoComponent } from '@sondermind/features/provider-listing/provider-photo';
import {
  AppointmentCarouselComponent,
  AppointmentSessionTypeSelectComponent,
  IAppointmentSessionTypeSelection,
  IAppointmentSlotSelection
} from '@sondermind/features/public-facing/provider-calendar';
import {
  IQuickCheckoutApiResponse,
  IQuickCheckoutFormValues,
  QuickCheckoutStore,
  SagaProgress,
  TIME_SLOT_UNAVAILABLE_ERROR_CODE,
  TIME_SLOT_UNAVAILABLE_ERROR_MESSAGE
} from '@sondermind/features/public-facing/session-request';
import { IconColors, IconSizes, IrisIconComponent } from '@sondermind/iris/icon';
import { IrisIconButtonModule, IrisIconButtonTypes } from '@sondermind/iris/icon-button';
import { IrisModalRef } from '@sondermind/iris/modal';
import { IrisProgressBarModule } from '@sondermind/iris/progress-bar';
import { CommunicationPreference } from '@sondermind/utilities/models-clinical';
import { ISessionRequestPreferences } from '@sondermind/utilities/models-scheduling';
import { SessionPreference, SessionType } from '@sondermind/utilities/models-session';
import { InitialsPipe } from '@sondermind/utilities/pipes';
import { WindowRefService } from '@sondermind/utilities/window';
import { IProviderSearch, } from '@sondermind/data-access/public-facing/provider-directory';
import { LocalizedDatePipe } from '@sondermind/utilities/time-and-date';
import {
  CoverageBadgeComponent,
  CoverageBadgeStore,
  CoverageInfo
} from '@sondermind/features/client-facing/coverage-badge';
import { FEATURE_FLAG_SERVICE, FeatureFlagKey, IFeatureFlagService } from '@sondermind/data-access/feature-flags';

import { NativeAppContextHelpers } from '@sondermind/native-app-interface';

import { SessionTaskType, SessionTaskComponent } from '@sondermind/features/client-facing/session-task';

import { FilterOptionsStore } from '@sondermind/data-access/public-facing/filter-form';
import {
  AccountSetupComponent,
  AccountSetupModule,
  SetupEbcgPaymentInfoComponent,
  SetupPaymentInfoComponent
} from '@sondermind/features/account-setup';

import { ProviderLocationsStore } from '@sondermind/data-access/client-facing/locations';
import { Location } from '@sondermind/utilities/models-location';
import {
  BookingFlow,
  BookingStates,
  CLIENT_SESSION_CONFLICT_ERROR_CODE,
  CLIENT_SESSION_CONFLICT_ERROR_MESSAGE,
  DirectBookingData,
  DirectBookingResult
} from './direct-booking-types';

enum SubmissionType {
  DIRECT_SCHEDULE = 'DIRECT_SCHEDULE',
  PROPOSE_TIME = 'PROPOSE_TIME',
  QUICK_CHECKOUT = 'QUICK_CHECKOUT',
  RESCHEDULE = 'RESCHEDULE',
  CANCEL = 'CANCEL'
}

enum DisplaySessionLocationType {
  VIDEO = 'Video session',
  IN_PERSON = 'In person session',
  HYBRID = 'In person or video session'
}
const TOOLTIP_MESSAGES = {
  inNetworkWithEstimate:
    `This is an estimate of your out-of-pocket cost and is not a guarantee of payment. ` +
    `Your final cost may vary based on your specific insurance plan and provider.`,
  inNetworkVerified:
    `We've verified your insurance and confirmed active coverage!\n\n` +
    `After your session, we'll submit your claim and your insurance will determine your final session cost.\n\n` +
    `For specific details about your costs, call the member services number on the back of your insurance card.`,
  insuranceNeedsUpdate:
    `We can't provide a cost estimate until your insurance information is up to date. ` +
    `You can add or update your insurance details on your account after you confirm this session.`,
  cashPay:
    `You will be paying for this session in cash. ` +
    `The full session fee will be charged directly to your payment method.`,
  outOfNetwork:
    `This session is with a provider who is not in your insurance network. ` +
    `You will be responsible for the full session fee, which can be paid directly to the provider.`,
  addingPaymentMethod:
    `Because your insurance or provider has changed, SonderMind will now process your claims ` +
    `and collect payment for anything not covered by your insurance.`,
  ebcgPaymentExplanation:
    `Because your insurance or provider has changed, a third party will now process your claims ` +
    `and collect payment for anything not covered by insurance.`,
} as const;

const MATCHING_FLOW_REFERRAL_TYPE_NAME = 'Client Referral';

export enum RedirectPath {
  DASHBOARD = '/dashboard',
  ACCOUNT_SETUP = '/profile/account-setup',
  INSURANCE = '/profile/insurance',
  PAYMENT = '/profile/payment',
  CHAT = '/chat/rooms',
}

@Component({
  selector: 'direct-booking',
  imports: [
    CommonModule,
    ProviderPhotoComponent,
    MatProgressSpinnerModule,
    InitialsPipe,
    IrisIconComponent,
    IrisIconButtonModule,
    AppointmentCarouselComponent,
    LocalizedDatePipe,
    TooltipModule,
    CoverageBadgeComponent,
    SessionTaskComponent,
    AccountSetupModule,
    IrisProgressBarModule,
    AppointmentSessionTypeSelectComponent,
  ],
  templateUrl: './direct-booking.component.html',
  styleUrl: './direct-booking.component.scss'
})
export class DirectBookingComponent implements OnInit, OnDestroy {
  constructor(
    private irisModalRef: IrisModalRef<DirectBookingComponent, DirectBookingData, DirectBookingResult>,
    public coverageNotificationService: CoverageNotificationService,
    public currentUser: CurrentUser,
    private creditCardStore: CreditCardStore,
    private router: Router,
    private windowRef: WindowRefService,
    private logger: Logger,
    private quickCheckoutStore: QuickCheckoutStore,
    private directScheduleSessionService: DirectScheduleSessionHttpService,
    private scheduledEventsService: ScheduledEventsHttpService,
    private providerStore: ProviderStore,
    private filterOptionsStore: FilterOptionsStore,
    private notificationToastService: NotificationToastService,
    private clientReferralMembersStore: ClientReferralMembersStore,
    private clientPortalReferralsStore: ClientPortalReferralsStore,
    private segmentService: SegmentService<IClientSegmentSchema>,
    private appointmentsHttpSvc: AppointmentsHttpService,
    private patientInsuranceStore: PatientInsuranceStore,
    private appointmentHttpService: AppointmentsHttpService,
    private coverageBadgeStore: CoverageBadgeStore,
    private schedulingAvailabilityService: SchedulingAvailabilityService,
    @Inject(FEATURE_FLAG_SERVICE)
    private featureFlagService: IFeatureFlagService,
    private clientProviderLocationsStore: ProviderLocationsStore,
  ) { }

  private readonly statesThatAllowTimeChange: ReadonlySet<BookingStates> = new Set([
    BookingStates.INITIAL,
    BookingStates.INITIAL_WITHOUT_TIMESLOTS,
    BookingStates.RESCHEDULE,
    BookingStates.ERROR
  ]);

  bookingStates = BookingStates;
  bookingFlows = BookingFlow;
  irisIconButtonTypes = IrisIconButtonTypes;
  timezone: string = '';
  readonly bookingState = signal<BookingStates>(BookingStates.INITIAL);
  readonly bookingStateBeforeAccountSetup = signal<BookingStates>(BookingStates.INITIAL);
  readonly changingTime = signal<boolean>(false);
  readonly appointmentSlots = signal<IAppointmentSlot[]>([]);
  readonly inNetwork = signal<boolean | undefined>(undefined);
  readonly loadingTimeslots = signal<boolean>(false);
  readonly loadingProviderProfile = signal<boolean>(false);
  readonly pendingPATSession = signal<Appointment | undefined>(undefined);
  readonly isSaasInvited = toSignal(this.providerStore.isClientSaasInvited$());
  /** True when no client insurance has requireCreditCard; false when at least one requires a card. */
  readonly noInsuranceRequiresCreditCard = toSignal(
    this.patientInsuranceStore.noInsuranceRequiresCreditCard$,
    { initialValue: true }
  );

  // ViewChild reference for direct method calls to account setup component
  readonly accountSetupComponent = viewChild<AccountSetupComponent>('accountSetupComponent');
  // ViewChild reference for EBCG payment component
  readonly ebcgPaymentComponent = viewChild<SetupEbcgPaymentInfoComponent>('ebcgPaymentComponent');
  // ViewChild reference for payment info component (ADDING_PAYMENT_METHOD step)
  readonly paymentInfoComponent = viewChild<SetupPaymentInfoComponent>('paymentInfoComponent');

  readonly isInNativeApp = NativeAppContextHelpers.isHostedInNativeApp();
  readonly sessionPreference = SessionPreference;

  bookingFlow: BookingFlow = BookingFlow.NEW_CLIENT;
  clientReferralMemberId: number | undefined;
  iconColors = IconColors;
  irisIconSizes = IconSizes;
  timeslot: ISessionRequestPreferences | undefined;
  provider!: IProvider | IProviderSearch;
  appointment: Appointment | undefined;
  cashPay: boolean = false;
  verifiedInsuranceAmount: number | undefined;
  sessionType: string = 'Video';
  certifications: string[] = [];
  benefitsInfo: IBenefitsInformation | undefined;
  readonly expiredCreditCard = signal<CreditCard | undefined>(undefined);
  destroyed$: Subject<void> = new Subject<void>();
  readonly submittingSession = signal(false);
  errorMessage: string = '';
  providerProfile: Provider | undefined = undefined;
  cachedVerifiedInsuranceNames: string | undefined = undefined;
  readonly sessionTaskType = SessionTaskType;
  readonly userNeedsSetup = toSignal(this.currentUser.user$.pipe(map((user) => user?.needsSetup)));

  private readonly requireAccountCompletion = signal<boolean>(false);
  readonly loadingAccountCompletionCheck = signal<boolean>(true);
  readonly forceAccountCompletion = computed(
    () => this.userNeedsSetup() && this.requireAccountCompletion()
  );

  // Used to store locations to select in-person sessions
  readonly providerLocations = signal<Location[]>([]);
  readonly isLoadingLocations = signal<boolean>(false);
  readonly showSessionTypeSelect = toSignal(
    this.featureFlagService.isEnabled$(FeatureFlagKey.VARIABLE_SESSION_DURATION)
  );
  // Stores the selected location for the session -- if in-person, will be the particular location's id
  readonly selectedLocation = signal<string>('');
  readonly isInPersonSession = signal<boolean>(false);

  // Finds and stores the actual location string for an in-person session location
  readonly inPersonSessionLocation = computed<string>(() => {
    const isInPersonSession = this.isInPersonSession();
    const selectedLocationId = this.selectedLocation();
    const locations = this.providerLocations();

    if (isInPersonSession) {
      const location = locations.find((l) => l.id.toString() === selectedLocationId);
      if (location) {
        return `${location.addressLine1}, ${location.city}, ${location.state} ${location.postalCode}`;
      }
    }

    return '';
  });

  // EBCG payment signals - mirrors account-setup logic
  readonly ebcgFeatureEnabled = signal<boolean>(false);
  readonly validForEbcgPaymentCollection = signal<boolean>(false);
  readonly loadingEbcgEligibility = signal<boolean>(false);

  /**
   * Determine if the user needs to add an EBCG payment method.
   * Requires:
   * - EBCG_FEATURE_FOR_EXISTING_CLIENTS feature flag enabled
   * - Provider is an EBCG Embrace provider
   * - Client has valid insurance for EBCG payment collection
   * - Client does NOT already have an EBCG payment method
   * This mirrors the same logic in account-setup component.
   */
  readonly needsEbcgPayment = computed(
    () => this.ebcgFeatureEnabled() &&
      this.currentUser.user?.needsSetup === false &&
      this.providerProfile?.ebcgEmbraceProvider &&
      this.validForEbcgPaymentCollection() &&
      this.currentUser.user?.hasEbcgPaymentMethod === false
  );

  /**
   * True when client has EBCG payment but no credit card and is booking with a non-EBCG provider.
   * In that case we prompt them to add a credit card before completing the booking.
   * Always false for VA clients—we never collect a payment method from a VA client.
   */
  readonly needsCreditCardForBooking = computed(
    () => !this.currentUser.isVeteransAffairs() &&
      this.bookingFlow === BookingFlow.EXISTING_CLIENT &&
      !this.currentUser.user?.needsSetup &&
      !this.currentUser.user?.hasCreditCard &&
      this.currentUser.user?.hasEbcgPaymentMethod &&
      this.providerProfile?.ebcgEmbraceProvider === false
  );

  /**
   * Computed signal that combines all initial loading states.
   * Used to show loading indicators and disable actions while data is being fetched.
   */
  readonly isLoadingInitialData = computed(
    () => this.loadingProviderProfile() || this.loadingAccountCompletionCheck() || this.loadingEbcgEligibility()
  );

  // Account setup properties
  readonly isInsuranceEmpty = signal<boolean>(false);
  readonly setupProcessing = signal<boolean>(false);
  readonly costEstimateContext = signal<ICostEstimateContextAttributes | null>(null);

  readonly verifiedInsuranceNames = signal<string>('');
  readonly unverifiedInsuranceNames = signal<string>('');
  readonly expiredInsuranceNames = signal<string>('');

  readonly hasUnverifiedInsurance = signal<boolean>(false);
  readonly hasInactiveInsurance = signal<boolean>(false);
  readonly hasExpiredInsurance = signal<boolean>(false);
  readonly hasValidInsurance = signal<boolean>(false);

  readonly hasMultipleIssues = computed(
    () => this.hasUnverifiedInsurance() || this.hasExpiredInsurance() || !!this.expiredCreditCard()
  );

  readonly canChangeTime = computed(
    () => this.statesThatAllowTimeChange.has(this.bookingState()) &&
      !this.changingTime() &&
      this.appointmentSlots().length > 0 &&
      !this.loadingTimeslots()
  );

  readonly canRescheduleInCancelState = computed(() => {
    if (this.bookingState() !== BookingStates.CANCELLING) return false;

    // If there's a pending PAT session, can't reschedule
    if (this.pendingPATSession()) return false;

    // Otherwise check if provider supports rescheduling
    if (!this.provider || !this.isIProvider(this.provider)) return false;
    return this.provider.hasDirectScheduling || this.provider.hasProposeATime;
  });

  readonly showCostEstimate = computed(
    () => this.inNetwork() === true &&
      (this.verifiedInsuranceAmount || this.verifiedInsuranceAmount === 0)
  );

  readonly coverageInfo = signal<CoverageInfo | undefined>(undefined);

  private isIProvider(provider: IProvider | IProviderSearch): provider is IProvider {
    return 'hasDirectScheduling' in provider;
  }

  readonly tooltipText = computed(() => {
    if (this.showCostEstimate()) {
      return TOOLTIP_MESSAGES.inNetworkWithEstimate;
    }
    if (this.inNetwork() === true && !this.verifiedInsuranceAmount && this.verifiedInsuranceAmount !== 0) {
      return TOOLTIP_MESSAGES.inNetworkVerified;
    }
    if (this.hasExpiredInsurance() || this.hasInactiveInsurance() || this.hasUnverifiedInsurance()) {
      return TOOLTIP_MESSAGES.insuranceNeedsUpdate;
    }
    if (this.cashPay) {
      return TOOLTIP_MESSAGES.cashPay;
    }
    // Out of network
    if (this.inNetwork() === false) {
      return TOOLTIP_MESSAGES.outOfNetwork;
    }
    return '';
  });

  /** True when the shared payment explanation block (EBCG or ADDING_PAYMENT_METHOD)
   * should show for existing clients with complete accounts. */
  readonly showPaymentExplanationBlock = computed(() => {
    const inInitialBookingState =
      this.bookingState() === BookingStates.INITIAL ||
      this.bookingState() === BookingStates.INITIAL_WITHOUT_TIMESLOTS;
    const hasSlotAndNotChanging = !!this.timeslot && !this.changingTime();
    const needsPaymentStep =
      (this.needsEbcgPayment() && this.bookingFlow === BookingFlow.EXISTING_CLIENT) ||
      this.needsCreditCardForBooking();

    return hasSlotAndNotChanging && inInitialBookingState && needsPaymentStep;
  });

  /** Tooltip text for "Why do I need to add a payment method?" in the shared block. */
  readonly paymentExplanationTooltipText = computed(() => {
    if (this.needsCreditCardForBooking()) {
      return TOOLTIP_MESSAGES.addingPaymentMethod;
    }
    if (this.needsEbcgPayment()) {
      return TOOLTIP_MESSAGES.ebcgPaymentExplanation;
    }
    return '';
  });

  /**
   * Only clients who are cash pay clients and don't already have a valid insurance registered
   * will be prompted to add insurance after scheduling.
   * In addition, SAAS-invited clients will always be cash pay, and they can't add insurance on top of that,
   * so don't ever prompt them to add insurance after scheduling.
   * */
  readonly canAddInsurance = computed(() => !this.hasValidInsurance() && this.cashPay && !this.isSaasInvited());

  // Account completion progress bar value
  readonly acProgressBarValue = signal<number>(0);
  // This will temporarily store the booking state at the time the user clicked 'x' button to discard the booking
  readonly previousBookingState = signal<BookingStates | undefined>(undefined);

  ngOnInit(): void {
    if (this.irisModalRef.config?.data) {
      this.timeslot = this.irisModalRef.config.data?.slot;
      this.provider = this.irisModalRef.config.data?.providerPreview;
      this.clientReferralMemberId = this.irisModalRef.config.data?.clientReferralMemberId;
      this.bookingFlow = this.irisModalRef.config.data?.bookingFlow;

      this.bookingState.set(this.irisModalRef.config.data?.bookingState ?? BookingStates.INITIAL);
      if (this.bookingState() === BookingStates.RESCHEDULE
        || this.bookingState() === BookingStates.INITIAL_WITHOUT_TIMESLOTS) {
        this.changeTime(true);
      }
      if (this.bookingState() === BookingStates.RESCHEDULE || this.bookingState() === BookingStates.CANCELLING) {
        this.appointment = this.irisModalRef.config.data?.appointment;
        this.timeslot = {
          startTime: this.appointment?.startTime,
          endTime: this.appointment?.endTime,
        };
        const appointmentLocationId = this.appointment?.location?.id;
        const isAppointmentInPerson = this.appointment?.sessionType?.toLowerCase() === 'in_person';
        if (isAppointmentInPerson && appointmentLocationId != null) {
          this.selectedLocation.set(appointmentLocationId.toString());
          this.isInPersonSession.set(true);
        } else if (this.appointment?.sessionType) {
          const isVideo = this.appointment.sessionType.toLowerCase() === SessionType.VIDEO;
          this.selectedLocation.set(isVideo ? SessionType.VIDEO : '');
          this.isInPersonSession.set(false);
        }
      }

      if (this.provider) {
        // make sure coverage has been loaded
        // note: this is "no-op" if we have previously loaded info for this provider
        this.coverageBadgeStore.loadCoverageStatusInfo([this.provider.providerId]);

        const coverageInfo = this.coverageBadgeStore.getCoverageInfo(this.provider.providerId);

        // a populated insurance name means "in-network"
        this.inNetwork.set(!!coverageInfo?.insuranceName);
      }

      this.getAppointmentSlots();
      this.getProviderLocations();
      this.checkIfTimeIsAlreadyProposed();
      this.handleCertificationsList();
      this.checkSessionType();
      this.checkCashPay();
      this.loadBenefitsInfo();
      this.checkInsuranceStatus();
      this.checkCreditCardExpired();
      this.loadPersonaProviderProfile();
      this.getTimezone();
      this.checkIfRequireAccountCompletion();
      this.checkEbcgPaymentRequirements();
    }
  }

  checkIfRequireAccountCompletion(): void {
    if (!this.provider) {
      this.loadingAccountCompletionCheck.set(false);
      return;
    }

    this.clientPortalReferralsStore
      .loadReferrals$()
      .pipe(take(1))
      .subscribe((referrals) => {
        const hasMatchingFlowReferral = referrals.some(
          (r) => r.type === MATCHING_FLOW_REFERRAL_TYPE_NAME
        );

        if (hasMatchingFlowReferral) {
          this.reloadUser();
          this.requireAccountCompletion.set(true);
        }

        this.loadingAccountCompletionCheck.set(false);

        const data: IDirectBookingStartedData = {
          bookingState: this.bookingState().toString(),
          personaClientId: this.currentUser.user?.personaId ?? 0,
          accountComplete: !this.currentUser.user?.needsSetup,
          requireAccountCompletionFlag: this.requireAccountCompletion()
        };

        this.segmentService.track<ClientSegmentEvents.DIRECT_BOOKING_STARTED>(
          ClientSegmentEvents.DIRECT_BOOKING_STARTED,
          data
        ).catch(() => {});
      });
  }

  /**
   * Check EBCG feature flag and determine if client requires EBCG payment.
   * Only runs if account is complete (account completion takes priority).
   * This mirrors the same logic in account-setup component.
   */
  checkEbcgPaymentRequirements(): void {
    // Skip check for EBCG feature flag and patient insurance eligibility if
    // client account is incomplete, provider is not an EBCG Embrace provider,
    // or client already has EBCG payment method
    if (this.currentUser.user?.needsSetup || !this.providerProfile?.ebcgEmbraceProvider || this.currentUser.user?.hasEbcgPaymentMethod) {
      return;
    }

    this.loadingEbcgEligibility.set(true);

    this.featureFlagService.isEnabled$(FeatureFlagKey.EBCG_FEATURE_FOR_EXISTING_CLIENTS)
      .pipe(
        take(1)
      )
      .subscribe({
        next: (isEnabled) => {
          this.ebcgFeatureEnabled.set(isEnabled);

          if (this.ebcgFeatureEnabled()) {
            this.loadInsuranceEligibilityForEbcg();
          } else {
            // If EBCG is disabled, stop loading
            this.loadingEbcgEligibility.set(false);
          }
        },
        error: (err) => {
          this.logger.error('Failed to check EBCG feature flag', err);
          this.ebcgFeatureEnabled.set(false);
          this.loadingEbcgEligibility.set(false);
        }
      });
  }

  /**
   * Check if client's insurance is valid for EBCG payment collection.
   * The insurance data is already loaded by PatientInsuranceStore constructor,
   * so we just need to check the current state.
   * This mirrors the same logic in account-setup component.
   */
  private loadInsuranceEligibilityForEbcg(): void {
    this.patientInsuranceStore.fullInsuranceList$
      .pipe(take(1))
      .subscribe({
        next: (insurances) => {
          const hasEbcgInsurance = insurances.some(
            (insurance) => insurance.providerNetworkCompany?.validForEbcgPaymentCollection === true
          );
          this.validForEbcgPaymentCollection.set(hasEbcgInsurance);
          this.loadingEbcgEligibility.set(false);
        },
        error: () => {
          this.validForEbcgPaymentCollection.set(false);
          this.loadingEbcgEligibility.set(false);
        }
      });
  }

  /**
   * Handle successful credit card submission from ADDING_PAYMENT_METHOD step.
   * Reloads user, returns to previous state, then completes the booking (direct schedule for existing client).
   */
  handlePaymentMethodAdded(): void {
    this.setupProcessing.set(true);

    this.currentUser.reload$().pipe(take(1)).subscribe({
      next: () => {
        this.setupProcessing.set(false);
        this.bookingState.set(this.bookingStateBeforeAccountSetup());
        // Complete the appointment booking; ADDING_PAYMENT_METHOD is only used for EXISTING_CLIENT flow
        this.submittingSession.set(true);
        this.directScheduleSession();
      },
      error: (err) => {
        this.logger.error('Failed to reload user after adding payment method', { err });
        this.setupProcessing.set(false);
        this.bookingState.set(BookingStates.ERROR);
        this.errorMessage = 'Sorry! There was an issue confirming your session';
      }
    });
  }

  /**
   * Handle successful EBCG payment submission.
   * Called when the EBCG payment component emits paymentInfoSubmitted.
   * Reloads user data to get updated hasEbcgPaymentMethod flag before proceeding.
   */
  handleEbcgPaymentSubmitted(): void {
    this.setupProcessing.set(true);

    // Reload user data to get updated hasEbcgPaymentMethod flag
    this.currentUser.reload$().pipe(
      take(1)
    ).subscribe({
      next: () => {
        this.setupProcessing.set(false);
        // Payment completed and user reloaded, return to previous state and proceed.
        // Skip EBCG check so we don't re-enter ADDING_EBCG_PAYMENT if user data is stale.
        this.bookingState.set(this.bookingStateBeforeAccountSetup());
        this.confirmSession(true);
      },
      error: (err) => {
        this.logger.error('Failed to reload user after EBCG payment', err);
        this.setupProcessing.set(false);
        this.bookingState.set(BookingStates.ERROR);
        this.errorMessage = 'Sorry! There was an issue confirming your session';
      }
    });
  }

  /**
   * Navigate from EBCG payment explanation screen to the actual payment form.
   */
  /**
   * Initialize account setup properties.
   * Sets up observables and loads insurance data needed for account setup component.
   */
  initializeAccountSetup(): void {
    // Set cost estimate context based on the selected timeslot
    if (this.timeslot?.startTime && this.provider) {
      /* eslint-disable @typescript-eslint/naming-convention */
      this.costEstimateContext.set({
        persona_provider_id: this.provider.providerId ?? 0,
        date_of_service: DateTime.fromJSDate(new Date(this.timeslot.startTime)).toISODate() ?? '',
        session_length: this.timeslot.endTime && this.timeslot.startTime
          ? Math.abs(
            DateTime.fromJSDate(new Date(this.timeslot.endTime))
              .diff(DateTime.fromJSDate(new Date(this.timeslot.startTime)), 'minutes')
              .toObject().minutes ?? 60
          )
          : 60,
        visit_type: this.timeslot.sessionType === SessionPreference.IN_PERSON
          ? 'in_person'
          : 'telehealth'
      });
      /* eslint-enable @typescript-eslint/naming-convention */
    }
  }

  checkInsuranceStatus(): void {
    combineLatest([
      this.coverageNotificationService.hasUnverifiedInsurance$,
      this.coverageNotificationService.hasExpiredInsurance$,
      this.coverageNotificationService.hasInactiveInsurance$,
      this.coverageNotificationService.hasValidInsurance$,
      this.coverageNotificationService.validInsurances$,
      this.coverageNotificationService.unverifiedInsurances$,
      this.coverageNotificationService.expiredInsurances$,
    ])
      .pipe(
        takeUntil(this.destroyed$),
      )
      .subscribe(([
        hasUnverifiedInsurance, hasExpiredInsurance, hasInactiveInsurance, hasValidInsurance,
        validInsurances, unverifiedInsurances, expiredInsurances
      ]) => {
        this.hasUnverifiedInsurance.set(hasUnverifiedInsurance);
        this.hasExpiredInsurance.set(hasExpiredInsurance);
        this.hasInactiveInsurance.set(hasInactiveInsurance);
        this.hasValidInsurance.set(hasValidInsurance);

        const mapper = (insurances: ITempImprovementPatientInsurances[]) => insurances
          .map((i) => i.providerNetworkCompany?.name)
          .filter((name): name is string => !!name)
          .join(', ') || '';

        this.expiredInsuranceNames.set(mapper(expiredInsurances));
        this.unverifiedInsuranceNames.set(mapper(unverifiedInsurances));

        this.cachedVerifiedInsuranceNames = mapper(validInsurances);
        this.verifiedInsuranceNames.set(this.cachedVerifiedInsuranceNames);
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  getTimezone(): void {
    const now = new Date();
    const options: Intl.DateTimeFormatOptions = { timeZoneName: 'short' };
    const formatter = new Intl.DateTimeFormat('en-US', options); // Or your desired locale
    const formattedDateParts = formatter.formatToParts(now);

    const timeZoneAbbreviation = formattedDateParts.find((part) => part.type === 'timeZoneName')?.value;
    this.timezone = timeZoneAbbreviation ?? '';
  }

  checkIfTimeIsAlreadyProposed(): void {
    if (!this.isIProvider(this.provider)) return;

    this.appointmentHttpService.pendingPATSessionsByProviderId$.pipe(
      take(1)
    ).subscribe((sessions: Record<number, Appointment>) => {
      const session = sessions[this.provider.providerId];

      // Store the pending session in the signal
      this.pendingPATSession.set(session);

      // If not in cancel state and there's a pending session, update booking state
      if (this.bookingState() !== BookingStates.CANCELLING && session) {
        this.bookingState.set(BookingStates.ALREADY_PROPOSED_TIME);
        this.changeTime(false);
      }
    });
  }

  loadPersonaProviderProfile(): void {
    if (!this.provider?.providerId) {
      return;
    }

    if ((this.provider as IProvider)?.hasDirectScheduling === undefined ||
      (this.provider as IProvider)?.hasDirectScheduling === null) {
      this.loadingProviderProfile.set(true);
      this.providerStore.loadProvider$(this.provider.providerAccountId).subscribe((provider) => {
        this.providerProfile = provider;
        this.loadingProviderProfile.set(false);
      });
    } else {
      this.providerProfile = this.provider as Provider;
    }
  }

  directScheduleSession(): void {
    if (!this.timeslot?.startTime || !this.timeslot?.endTime) {
      return;
    }

    this.errorMessage = '';
    const defaultType = this.timeslot?.sessionType === SessionPreference.IN_PERSON ? 'physical' : 'virtual';
    const locationId = this.getSessionLocationId(defaultType);

    if (defaultType === 'physical' && (!locationId || locationId === 0)) {
      this.errorMessage = 'Please select a session location before confirming.';
      return;
    }

    this.directScheduleSessionService.submitDirectScheduleSession({
      locationId: locationId ?? 0,
      personaProviderId: this.provider?.providerId ?? 0,
      startTime: DateTime.fromJSDate(this.timeslot?.startTime),
      endTime: DateTime.fromJSDate(this.timeslot?.endTime),
      sessionType: this.timeslot?.sessionType === SessionPreference.IN_PERSON
        ? SessionType.IN_PERSON
        : SessionType.VIDEO,
      timeProposal: !this.providerProfile?.hasDirectScheduling,
      personaClientId: this.currentUser.user?.personaId ?? 0,
      clientReferralMemberId: this.clientReferralMemberId ?? 0
    }).pipe(
      catchError(
        (error: HttpErrorResponse) => this.handleSubmitError(
          error,
          this.providerProfile?.hasDirectScheduling ? SubmissionType.DIRECT_SCHEDULE : SubmissionType.PROPOSE_TIME
        )
      ),
    ).subscribe((session) => {
      this.submittingSession.set(false);
      if (session) {
        this.refreshStores().subscribe();
        if (this.providerProfile?.hasDirectScheduling) {
          this.bookingState.set(BookingStates.CONFIRMED);
        } else {
          this.bookingState.set(BookingStates.REQUESTED);
        }
      } else {
        this.bookingState.set(BookingStates.ERROR);
        if (!this.errorMessage) {
          this.errorMessage = 'Sorry! There was an issue booking that time';
        }
      }
    });
  }

  rescheduleSession(): void {
    if (!this.appointment?.id || !this.timeslot?.startTime || !this.timeslot?.endTime) {
      return;
    }

    this.errorMessage = '';
    const sessionType = this.timeslot?.sessionType === SessionPreference.IN_PERSON
      ? SessionType.IN_PERSON
      : SessionType.VIDEO;
    const locationId = this.getSessionLocationId(sessionType);

    if (sessionType === SessionType.IN_PERSON && (!locationId || locationId === 0)) {
      this.errorMessage = 'Please select a session location before confirming.';
      return;
    }

    this.submittingSession.set(true);
    this.directScheduleSessionService.rescheduleSession$(
      this.appointment?.id, this.timeslot?.startTime, this.timeslot?.endTime, sessionType, locationId
    ).pipe(
      catchError((error: HttpErrorResponse) => this.handleSubmitError(error, SubmissionType.RESCHEDULE)),
    ).subscribe((session) => {
      this.submittingSession.set(false);
      if (session) {
        this.refreshStores().subscribe();
        this.bookingState.set(BookingStates.CONFIRMED);
      } else {
        this.bookingState.set(BookingStates.ERROR);
        if (!this.errorMessage) {
          this.errorMessage = 'Sorry! There was an issue rescheduling that time';
        }
      }
    });
  }

  private getSessionLocationId(defaultType: string): number | undefined {
    let filteredLocations = this.providerProfile?.locations.filter(
      (loc) => loc.locationType === defaultType && loc.state === this.currentUser.user?.profile?.mailingAddress?.state);

    if (filteredLocations?.length === 0) {
      filteredLocations = this.providerProfile?.locations
        .filter((loc) => loc.state === this.currentUser.user?.profile?.mailingAddress?.state);
    }

    // If the client has directly selected a locationId, use that
    // If the selected location is video, use the filteredLocation we calculated
    return this.selectedLocation() === SessionPreference.VIDEO
      ? filteredLocations?.[0]?.id
      : Number(this.selectedLocation());
  }

  cancelSession(): void {
    if (!this.appointment?.id || !this.timeslot?.startTime || !this.timeslot?.endTime) {
      return;
    }

    this.submittingSession.set(true);
    this.scheduledEventsService.cancel$(this.appointment.id, true).pipe(
      switchMap(() => this.refreshStores())
    ).subscribe({
      next: () => {
        this.submittingSession.set(false);
        this.closeModal(true);
      },
      error: () => {
        this.submittingSession.set(false);
        this.notificationToastService.showNotificationToast({
          message: 'Sorry! There was an issue canceling that time',
          type: ToastType.ERROR
        });
        this.closeModal(false);
      }
    });
  }

  checkSessionType(): void {
    if (this.timeslot?.sessionType === SessionPreference.IN_PERSON) {
      this.sessionType = DisplaySessionLocationType.IN_PERSON;
    } else if (this.timeslot?.sessionType === SessionPreference.VIDEO) {
      this.sessionType = DisplaySessionLocationType.VIDEO;
    } else {
      this.sessionType = DisplaySessionLocationType.HYBRID;
    }
  }

  checkCreditCardExpired(): void {
    this.creditCardStore.creditCardList.pipe(takeUntil(this.destroyed$)).subscribe((cards) => {
      if (!cards || cards.length === 0) {
        return;
      }
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth() + 1;
      const expiredCards = cards.filter(
        (card) => card.expYear < currentYear || (card.expYear === currentYear && card.expMonth < currentMonth)
      );
      const validCards = cards.filter(
        (card) => card.expYear > currentYear || (card.expYear === currentYear && card.expMonth > currentMonth)
      );
      if (expiredCards.length > 0 && validCards.length === 0) {
        this.expiredCreditCard.set(expiredCards[0]);
      }
    });
  }

  checkCashPay(): void {
    forkJoin({
      user: this.currentUser.user$.pipe(take(1)),
      pncCashPay: this.coverageNotificationService.hasCashPayInsurance$.pipe(take(1))
    }).subscribe((results) => {
      this.cashPay = results.user?.hasCashPref || results.pncCashPay;
    });
  }

  loadBenefitsInfo(): void {
    this.coverageNotificationService.checkCoverageNotification();

    this.patientInsuranceStore.getBenefitsInfo$()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((benefitsInfo) => {
        this.benefitsInfo = benefitsInfo;
        this.checkVerifiedInsuranceAmount();
      });
  }

  checkVerifiedInsuranceAmount(): void {
    this.coverageNotificationService.hasValidInsurance$
      .pipe(take(1))
      .subscribe((hasValidInsurance) => {
        if (hasValidInsurance) {
          this.verifiedInsuranceAmount = this.benefitsInfo?.displayedClientCostEstimate ?? undefined;
          this.emitVerifiedInsuranceAmountSegmentEvent();
        }
      });
  }

  private emitVerifiedInsuranceAmountSegmentEvent(): void {
    this.segmentService.track<ClientSegmentEvents.CLIENT_VIEWED_DIRECT_BOOKING_COST_ESTIMATE>(
      ClientSegmentEvents.CLIENT_VIEWED_DIRECT_BOOKING_COST_ESTIMATE,
      {
        coverageType: this.benefitsInfo?.benefitStructure?.name ?? '',
        payerName: this.cachedVerifiedInsuranceNames ?? '',
        bookingState: this.bookingState().toString()
      }
    ).catch(() => {});
  }

  updateCreditCard(): void {
    this.router.navigate([RedirectPath.PAYMENT]).catch((err) => {
      this.logger.error(err);
    });
    this.closeModal(true, RedirectPath.PAYMENT);
  }

  updateInsurance(): void {
    this.router.navigate([RedirectPath.INSURANCE]).catch((err) => {
      this.logger.error(err);
    });
    this.closeModal(true, RedirectPath.INSURANCE);
  }

  navigateToAccountSetup(): void {
    this.router.navigate([RedirectPath.ACCOUNT_SETUP]).catch((err) => {
      this.logger.error(err);
    });
    this.closeModal(true, RedirectPath.ACCOUNT_SETUP);
  }

  showConfirmDiscardBooking(): void {
    this.previousBookingState.set(this.bookingState());
    this.bookingState.set(BookingStates.CONFIRM_DISCARD_BOOKING);
  }

  dismissModal(): void {
    const shouldNavigateToDashboard = [
      BookingStates.CONFIRMED,
      BookingStates.REQUESTED,
      BookingStates.ADDED_TO_MATCHES
    ].includes(this.bookingState());

    if (shouldNavigateToDashboard) {
      this.router.navigate([RedirectPath.DASHBOARD]).catch((err) => {
        this.logger.error(err);
      });
    }
    this.closeModal(shouldNavigateToDashboard);
  }

  updateProgressBar(value: number): void {
    this.acProgressBarValue.set(value);
  }

  /**
   * Handle exit from account setup flow (e.g., after EBCG payment error)
   * Closes the modal and navigates to care team page
   */
  exitSetupAndNavigate(): void {
    this.closeModal(false);
    this.router.navigate(['/care-team/providers']);
  }

  /**
   * Reload user data after account setup submission.
   * Fetches updated user info and insurance list to determine if insurance is empty.
   */
  reloadUser(): void {
    this.setupProcessing.set(true);

    this.currentUser.reload$().pipe(
      switchMap(() => this.patientInsuranceStore.loadPatientInsuranceList$()),
      switchMap(() => this.patientInsuranceStore.isInsuranceListEmpty$()),
      take(1)
    ).subscribe({
      next: (isEmpty) => {
        this.isInsuranceEmpty.set(isEmpty);
        this.setupProcessing.set(false);
      },
      error: () => {
        this.setupProcessing.set(false);
      }
    });
  }

  handleCertificationsList(): void {
    const providerCertificationAbbrevs = (this.provider?.licensures || [])?.map(
      (cert) => cert.abbreviation).filter((cert) => cert !== ''
    );
    if (providerCertificationAbbrevs?.length > 1) {
      const uniqueCertNames = [...new Set(providerCertificationAbbrevs)];
      this.certifications = uniqueCertNames.length > 1 ? uniqueCertNames?.slice(0, 2) : uniqueCertNames;
      return;
    }

    this.certifications = providerCertificationAbbrevs || [];
  }

  getAppointmentSlots(sessionType?: SessionType): void {
    if (!this.provider?.providerId) {
      return;
    }
    this.loadingTimeslots.set(true);

    const { clientReferralMemberId } = this;
    const hasValidClientReferralMemberId = clientReferralMemberId !== null
            && clientReferralMemberId !== undefined && clientReferralMemberId !== 0;

    const relationship$ = hasValidClientReferralMemberId
      ? this.clientReferralMembersStore.loadMember$(clientReferralMemberId).pipe(
        take(1),
        map((crm: ClientReferralMember) => {
          if (crm.status === ClientReferralMemberStatus.CARE_TEAM) {
            return ProviderRelationship.CARE_TEAM;
          }
          if (crm.status === ClientReferralMemberStatus.MATCHED) {
            return ProviderRelationship.MATCHED;
          }
          return ProviderRelationship.OTHER;
        }),
        catchError(() => of(ProviderRelationship.OTHER))
      )
      : of(ProviderRelationship.OTHER);

    combineLatest([
      relationship$,
      // Check the feature flag but move on if we take too long to read the flag
      this.featureFlagService.isEnabled$(FeatureFlagKey.VARIABLE_SESSION_DURATION).pipe(
        timeout({
          first: 1000,
          with: () => of(false),
        })
      )
    ]).pipe(
      switchMap(([relationship, flagEnabled]) => {
        const providerMembers: ProviderAvailabilityMember[] = [{
          providerId: this.provider.providerId,
          providerRelationship: relationship
        }];

        // If the feature flag for showing the session type select is enabled, and a provider offers video sessions
        // Start by filtering by video only, if no session type has been provided
        // Otherwise, just use hybrid
        const fallbackSessionType = flagEnabled
          && this.provider.sessionTypesOffered.includes(SessionType.VIDEO)
          ? SessionType.VIDEO
          : SessionType.NO_PREFERENCE;

        return this.schedulingAvailabilityService.getAvailabilityForProviders(
          providerMembers,
          undefined,
          add(new Date(), { weeks: 8 }), // Scheduling svc will truncate based on provider scheduling profile,
          sessionType ?? fallbackSessionType,
        );
      })
    ).subscribe({
      next: (availability) => {
        let timeSlots = availability.providerSlots.get(this.provider.providerId) ?? [];
        if (this.selectedLocation().length && this.selectedLocation() !== SessionType.VIDEO) {
          // Filter the time slots by only those for the correct location, if in-person.
          // NOTE: This is a temporary measure to filter slots by location id.
          // The scheduling service will be updated to support filtering by location in the future.
          timeSlots = this.filterInPersonSlotsByLocationId(timeSlots);
        }

        // convert to format appointment-carousel component expects
        const slots: IAppointmentSlot[] = timeSlots.map((timeSlot) => ({
          displayStartTime: timeSlot.start,
          utcStartTime: timeSlot.start,
          utcEndTime: timeSlot.end
        }));

        this.loadingTimeslots.set(false);
        this.appointmentSlots.set(slots);

        const hasSelectedSlot = !!this.timeslot?.startTime && !!this.timeslot?.endTime;
        if (slots?.length === 0 && (this.bookingState() === BookingStates.RESCHEDULE || !hasSelectedSlot)) {
          if (this.bookingFlow === BookingFlow.NEW_CLIENT) {
            this.bookingState.set(BookingStates.NEW_CLIENT_NO_TIMESLOTS);
          } else {
            this.bookingState.set(BookingStates.EXISTING_CLIENT_NO_TIMESLOTS);
          }
        } else if (slots && slots.length > 0) {
          // If there are new slots and we previously had no slots
          if (this.bookingState() === BookingStates.EXISTING_CLIENT_NO_TIMESLOTS) {
            // Return back to reschedule state
            this.bookingState.set(BookingStates.RESCHEDULE);

            if (hasSelectedSlot) {
              // Try to find the selected slot within the new returned slots
              const { startTime, endTime } = this.timeslot ?? {};
              const isCurrentSlotInNewSlots =
              startTime &&
              endTime &&
              slots.some((s) => {
                const isStartMatch = new Date(s.utcStartTime).getTime() === new Date(startTime).getTime();
                const isEndMatch = new Date(s.utcEndTime).getTime() === new Date(endTime).getTime();
                return isStartMatch && isEndMatch;
              });
              if (!isCurrentSlotInNewSlots) {
                this.timeslot = undefined;
              }
            }
          } else if (this.bookingState() === BookingStates.NEW_CLIENT_NO_TIMESLOTS) {
            // Return back to initial state
            this.bookingState.set(BookingStates.INITIAL_WITHOUT_TIMESLOTS);
          }
        }
      },
      error: (err) => {
        this.loadingTimeslots.set(false);
        this.logger.error('Failed to fetch availability from scheduling service', {
          providerId: this.provider?.providerId,
          bookingFlow: this.bookingFlow,
          bookingState: this.bookingState(),
          clientReferralMemberId: this.clientReferralMemberId,
          error: err
        });
      }
    });
  }

  /**
   * Gets all locations where a provider can provide their services. Used to allow clients to filter
   * appointment slots based on the location of their session.
   */
  getProviderLocations(): void {
    if (!this.provider?.providerId || this.bookingState() === BookingStates.CANCELLING) {
      return;
    }

    this.featureFlagService.isEnabled$(FeatureFlagKey.VARIABLE_SESSION_DURATION)
      .pipe(
        takeUntil(this.destroyed$),
        switchMap((isEnabled) => {
          if (isEnabled) {
            this.isLoadingLocations.set(true);
            return this.clientProviderLocationsStore.getProviderLocations$(this.provider.providerId);
          }
          return of([]);
        })
      )
      .subscribe((locations) => {
        this.providerLocations.set(locations);
        this.isLoadingLocations.set(false);
      });
  }

  handleAppointmentSessionTypeSelected(event: IAppointmentSessionTypeSelection): void {
    this.isInPersonSession.set(event.sessionType === SessionType.IN_PERSON);
    this.sessionType = this.isInPersonSession()
      ? DisplaySessionLocationType.IN_PERSON
      : DisplaySessionLocationType.VIDEO;

    // If a session is for a valid in person location, store the locationId associated with the session.
    // If it is virtual, just store the session type 'Video'.
    if (event.locationId && this.providerLocations().find((location) => location.id === event.locationId)) {
      this.selectedLocation.set(event.locationId.toString());
    } else {
      this.selectedLocation.set(event.sessionType);
    }

    if (!this.loadingTimeslots()) {
      this.getAppointmentSlots(this.isInPersonSession() ? SessionType.IN_PERSON : SessionType.VIDEO);
    }
  }

  private filterInPersonSlotsByLocationId(slots: TimeSlot[]): TimeSlot[] {
    return slots.filter(
      (slot) => slot.locationId && slot.locationId.toString() === this.selectedLocation()
    );
  }

  handleAppointmentSlotSelectionChanged(newSelection: IAppointmentSlotSelection | null): void {
    if (!newSelection?.startTime) {
      return;
    }
    this.changeTime(false);

    const previousSessionType = this.timeslot?.sessionType;
    const selectedSessionType = this.sessionType === DisplaySessionLocationType.IN_PERSON
      ? SessionPreference.IN_PERSON
      : SessionPreference.VIDEO;
    const anHourFromStart = new Date(newSelection?.startTime.getTime() + 60 * 60 * 1000);

    this.timeslot = {
      startTime: newSelection?.startTime,
      endTime: newSelection?.endTime ?? anHourFromStart,
      sessionType: selectedSessionType ?? previousSessionType,
    } as ISessionRequestPreferences;
  }

  rescheduleCanceledSession(): void {
    this.bookingState.set(BookingStates.RESCHEDULE);
    this.changeTime(true);
    this.getAppointmentSlots();
    this.getProviderLocations();
  }

  changeTime(value: boolean): void {
    if (value && !this.statesThatAllowTimeChange.has(this.bookingState())) {
      return;
    }
    this.changingTime.set(value);
  }

  closeModal(success: boolean, redirectPath?: RedirectPath): void {
    const successMessages: Partial<Record<BookingStates, string>> = {
      [BookingStates.CONFIRMED]: 'Your session has been successfully scheduled',
      [BookingStates.REQUESTED]: 'Your session has been requested',
      [BookingStates.ADDED_TO_MATCHES]: 'Provider added to matches successfully',
      [BookingStates.CANCELLING]: 'Your session has been cancelled'
    };

    const bookingResult: DirectBookingResult = {
      bookingState: this.bookingState(),
      message: success ? successMessages[this.bookingState()] || '' : '',
      success,
      redirectPath
    };

    this.irisModalRef.close(bookingResult);
  }

  submitQuickCheckout(validInsurances: ITempImprovementPatientInsurances[]): void {
    this.errorMessage = '';

    const user = this.currentUser?.user;
    const userProfile = user?.profile;
    const userCommPrefs = userProfile?.communicationPreferences;

    const formValues: IQuickCheckoutFormValues = {
      birthDate: userProfile?.birthDate,
      commPrefs: {
        commPrefsPhone: userCommPrefs?.includes(CommunicationPreference.PHONE) ?? false,
        commPrefsSms: userCommPrefs?.includes(CommunicationPreference.SMS) ?? false,
      },
      contactSmsAllowed: userCommPrefs?.includes(CommunicationPreference.SMS) ?? false,
      email: user?.email,
      firstName: userProfile?.firstName,
      insurance: validInsurances.length > 0 ? validInsurances[0].id : null,
      lastName: userProfile?.lastName,
      location: user?.insuranceState ?? userProfile?.mailingAddress?.state ?? this.filterOptionsStore.getCurrentState() ?? '',
      personaProviderId: this.provider?.providerId ?? 0,
      phone: userProfile?.phoneNumber,
      activeDuty: null,
      heardFrom: {
        source: null,
        details: null
      }
    };
    let sessionRequestPreferences: ISessionRequestPreferences | {} = {};
    if (this.timeslot?.startTime && this.timeslot?.endTime) {
      sessionRequestPreferences = {
        startTime: new Date(this.timeslot?.startTime),
        endTime: new Date(this.timeslot?.endTime),
        sessionType: this.timeslot?.sessionType,
      };
    }

    this.quickCheckoutStore.submitQuickCheckout$(
      formValues,
      sessionRequestPreferences,
      this.windowRef.nativeWindow?.location?.href ?? ''
    ).pipe(
      catchError((error: HttpErrorResponse) => this.handleSubmitError(error, SubmissionType.QUICK_CHECKOUT)),
      switchMap((resp: IQuickCheckoutApiResponse | null) => this.pollSagaStatus(resp))
    ).subscribe((resp: IQuickCheckoutApiResponse | null) => {
      this.submittingSession.set(false);
      if (this.bookingState() === BookingStates.NEW_CLIENT_NO_TIMESLOTS && resp?.referral_request_uuid) {
        this.bookingState.set(BookingStates.ADDED_TO_MATCHES);
        this.refreshStores().subscribe();
        this.closeModal(true);
      } else if (resp?.referral_request_uuid) {
        this.bookingState.set(BookingStates.CONFIRMED);
        this.refreshStores().subscribe();
      } else {
        this.bookingState.set(BookingStates.ERROR);
        if (!this.errorMessage) {
          this.errorMessage = 'Sorry! There was an issue booking that time';
        }
      }
    });
  }

  messageProvider(): void {
    this.router.navigate([RedirectPath.CHAT, this.provider?.providerId], {
    }).catch((err) => {
      this.logger.error(err);
    });
    this.closeModal(false);
  }

  refreshStores(): Observable<{
    appointments: Appointment[];
    referrals: ClientReferralLite[];
    referralMembers: ClientReferralMember | ClientReferralMember[];
  }> {
    // need to load appointments first, then order doesn't matter
    return this.appointmentsHttpSvc.refresh().pipe(
      switchMap((appointments) => forkJoin({
        appointments: of(appointments),
        referrals: this.clientPortalReferralsStore.refresh(),
        referralMembers: this.clientReferralMemberId
          ? this.clientReferralMembersStore.refreshMember(this.clientReferralMemberId)
          : this.clientReferralMembersStore.getAll$(true)
      }))
    );
  }

  /**
   * Proceeds with session confirmation. When called after EBCG payment was just added,
   * pass skipEbcgPaymentCheck so we don't re-enter ADDING_EBCG_PAYMENT due to stale user data.
   * @param skipEbcgPaymentCheck When true, skip the check that redirects to ADDING_EBCG_PAYMENT
   */
  confirmSession(skipEbcgPaymentCheck = false): void {
    if (this.bookingFlow === BookingFlow.EXISTING_CLIENT && (!this.timeslot?.startTime || !this.timeslot?.endTime)) {
      this.notificationToastService.showNotificationToast({
        message: 'Please select a time',
        type: ToastType.ERROR
      });
      return;
    }

    // Check if account completion is needed first
    if (this.forceAccountCompletion()) {
      this.initializeAccountSetup();
      this.bookingStateBeforeAccountSetup.set(this.bookingState());
      this.bookingState.set(BookingStates.COMPLETING_ACCOUNT);
      return;
    }

    // Check if EBCG payment is needed (only for existing clients with complete accounts).
    // Skip when proceeding after just adding EBCG payment to avoid resetting state due to stale user data.
    if (!skipEbcgPaymentCheck &&
        this.bookingFlow === BookingFlow.EXISTING_CLIENT &&
        !this.currentUser.user?.needsSetup &&
        this.needsEbcgPayment() &&
        this.bookingState() !== BookingStates.ADDING_EBCG_PAYMENT) {
      this.bookingStateBeforeAccountSetup.set(this.bookingState());
      this.bookingState.set(BookingStates.ADDING_EBCG_PAYMENT);
      return;
    }

    // Check if credit card is needed for client to book with this provider (client has EBCG payment but no card)
    if (this.needsCreditCardForBooking() &&
        this.bookingState() !== BookingStates.ADDING_PAYMENT_METHOD) {
      this.bookingStateBeforeAccountSetup.set(this.bookingState());
      this.bookingState.set(BookingStates.ADDING_PAYMENT_METHOD);
      return;
    }

    // If we were in the account setup or EBCG payment process, return to previous state
    if (this.bookingState() === BookingStates.COMPLETING_ACCOUNT ||
        this.bookingState() === BookingStates.ADDING_EBCG_PAYMENT) {
      this.bookingState.set(this.bookingStateBeforeAccountSetup());
    }

    this.submittingSession.set(true);

    // Use explicit booking flow to determine which booking method to use
    if (this.bookingFlow === BookingFlow.EXISTING_CLIENT) {
      this.directScheduleSession();
      return;
    }

    // Quick checkout flow for new clients
    this.coverageNotificationService.validInsurances$.pipe(take(1)).subscribe((validInsurances) => {
      this.submitQuickCheckout(validInsurances);
    });
  }

  /**
   * Polls the saga status API to check if the quick checkout request has completed
   * @param resp The quick checkout API response containing the saga_id
   * @returns Observable that emits the response if saga completes successfully, or null if it fails/times out
   */
  pollSagaStatus(resp: IQuickCheckoutApiResponse | null): Observable<IQuickCheckoutApiResponse | null> {
    if (!resp) {
      return of(resp);
    }

    const maxPolls = 10;

    return timer(0, 1000).pipe(
      take(maxPolls),
      mergeMap(() => this.quickCheckoutStore.getSagaStatus$(resp.saga_id).pipe(
        map((status) => ({ status, originalResponse: resp }))
      )),
      takeWhile(({ status }) => {
        if (!status) return false;
        return status.progress === SagaProgress.PENDING || status.progress === SagaProgress.IN_PROGRESS;
      }, true),
      // Get the last emitted value (the final status)
      last(),
      // Map back to the appropriate response
      map(({ status, originalResponse }) => {
        if (status?.progress !== SagaProgress.FAILED) {
          return originalResponse;
        }
        // If FAILED, return null to trigger error handling
        return null;
      }),
      // Add an extra delay due to the sync between monolith writer and reader to ensure syncing is complete
      switchMap((response) => {
        if (response !== null) {
          // Delay only when response is not null (i.e., saga completed successfully)
          return of(response).pipe(delay(300));
        }
        // If FAILED, return immediately without delay
        return of(response);
      })
    );
  }

  private handleSubmitError(error: HttpErrorResponse, submissionType: SubmissionType): Observable<null> {
    this.submittingSession.set(false);
    this.logger.error(`Direct booking submission error: ${JSON.stringify(error)}`, {
      status: error.status,
      bookingState: this.bookingState(),
      bookingFlow: this.bookingFlow,
      providerId: this.provider?.providerId,
      timeslot: this.timeslot,
      submissionType: submissionType.toString(),
    });
    if (error.status === TIME_SLOT_UNAVAILABLE_ERROR_CODE
        && error.message?.toLocaleLowerCase()?.includes(TIME_SLOT_UNAVAILABLE_ERROR_MESSAGE)
    ) {
      // Fetch new appointment slots before showing the calendar
      this.getAppointmentSlots();
      this.changeTime(true);
      this.errorMessage = 'Sorry! There was an issue booking that time';
      this.bookingState.set(BookingStates.ERROR);
    } else if (error.status === CLIENT_SESSION_CONFLICT_ERROR_CODE) {
      this.getAppointmentSlots();
      this.changeTime(true);
      this.errorMessage = CLIENT_SESSION_CONFLICT_ERROR_MESSAGE;
      this.bookingState.set(BookingStates.ERROR);
    }
    return of(null);
  }
}
